import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Typography, Select, DatePicker, Tag, Row, Col, theme, Button, FilterResetButton, Tooltip, Descriptions, Image, EmptyState, Space, Progress, Modal, Form, Input, message, Segmented, StatCard, statTone, type TagTone, type StatTone } from '../../ui';
import { Eye, MapPin, Store, User, ShoppingCart, Crosshair, Plus, Pencil, Trash2, AlertTriangle, CalendarDays, List, Clock, Download, XCircle, CheckCircle2, Target, CalendarRange, Repeat, Search, Phone, Sprout } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUrlString, useUrlRange, patchSearchParams } from '../../hooks/useUrlState';
import { useDebounce } from '../../hooks/useDebounce';
import { hueOption } from '../../components/FilterPanel';
import dayjs, { Dayjs } from 'dayjs';
import { VisitPhotoCapture } from '../../components/VisitPhotoCapture';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { RoleBadge, roleLabel } from '../../components/RoleBadge';
import { RetailerLink, FarmerLink, UserLink } from '../../components/EntityLinks';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission, useAuthStore } from '../../store/auth';
import { useUrlTab } from '../../hooks/useUrlTab';
import { useServerSort } from '../../hooks/useServerSort';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { formatIst, formatIstTime, formatIstDate, toIstDateString } from '../../lib/time';
import { VisitRouteMap, distanceMeters, fmtDist, LatLng } from '../../components/VisitRouteMap';
import { ExportDialog } from '../../components/ExportDialog';
import { CoverageSection, type CoverageSectionKey } from './CoveragePage';
import { PALETTE, TAG_TONE_HUE } from '../../lib/palette';
import { stateName } from '../../constants/indianStates';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { useRetailerSearch, useFarmerSearch } from '../../hooks/useRetailerSearch';
import { displayMobile } from '../../lib/validators';
import { TerritoryChips } from '../../components/TerritoryChips';
import { ShiftChip } from '../../components/VisitShiftGroups';

const TYPE_COLOR: Record<string, TagTone> = { RETAILER: 'blue', FARMER: 'green', PROSPECT: 'gold' };
const mapsUrl = (lat?: string | null, lng?: string | null) =>
  lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : undefined;
const effColor = (s: number) => (s >= 80 ? PALETTE.brand[600] : s >= 50 ? PALETTE.harvest[400] : PALETTE.danger[500]);

/**
 * A party's place, in words. The API composes it as "city, <state>" where state has historically
 * been the raw GST state code — "Kalmeshwar, 27" — which means something to the tax engine and
 * nothing to the person reading a visit list. Any bare two-digit part is resolved to the state name
 * (27 → Maharashtra). Kept client-side as well as fixed server-side, so an older API still reads
 * correctly here; once it sends the name there is no numeric part left and this is a no-op.
 */
const placeLabel = (loc?: string | null) =>
  (loc ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => (/^\d{1,2}$/.test(part) ? stateName(part.padStart(2, '0')) : part))
    .join(', ');
const effTone = (s: number): TagTone => (s >= 80 ? 'green' : s >= 50 ? 'gold' : 'red');

/*
 * The register's filter vocabulary — each option in the reader's words, wearing the hue that value
 * already has in the rows below (the type tag, the Open tag, the score colour, the geofence and order
 * tags), so a filter and the rows it produces are one colour. Photos has no colour of its own on this
 * page, so its options keep the hollow ring.
 */
const TYPE_WORDS: Record<string, string> = { RETAILER: 'Retailer', FARMER: 'Farmer', PROSPECT: 'Prospect' };
const TYPE_OPTIONS = Object.entries(TYPE_WORDS).map(([v, text]) => hueOption(v, text, TAG_TONE_HUE[TYPE_COLOR[v]]));
const STATUS_WORDS: Record<string, string> = { completed: 'Completed', open: 'Open' };
const STATUS_OPTIONS = [
  hueOption('completed', STATUS_WORDS.completed, TAG_TONE_HUE.green),
  hueOption('open', STATUS_WORDS.open, TAG_TONE_HUE.blue),
];
/** The same cut-offs as `effTone` and the server's `visitQualityBand`: green 80+, gold 50+, red below. */
const QUALITY_WORDS: Record<string, string> = { good: 'Good · 80%+', fair: 'Fair · 50–79%', weak: 'Weak · under 50%' };
const QUALITY_OPTIONS = [
  hueOption('good', QUALITY_WORDS.good, TAG_TONE_HUE[effTone(80)]),
  hueOption('fair', QUALITY_WORDS.fair, TAG_TONE_HUE[effTone(50)]),
  hueOption('weak', QUALITY_WORDS.weak, TAG_TONE_HUE[effTone(0)]),
];
const GEOFENCE_WORDS: Record<string, string> = { in: 'Inside geofence', out: 'Outside geofence' };
const GEOFENCE_OPTIONS = [
  hueOption('in', 'Inside', TAG_TONE_HUE.green),
  hueOption('out', 'Outside', TAG_TONE_HUE.red),
];
const ORDER_WORDS: Record<string, string> = { true: 'With order', false: 'No order' };
const ORDER_OPTIONS = [
  hueOption('true', ORDER_WORDS.true, TAG_TONE_HUE.green),
  hueOption('false', ORDER_WORDS.false, TAG_TONE_HUE.neutral),
];
const PHOTO_WORDS: Record<string, string> = { with: 'With photos', without: 'No photos' };
const PHOTO_OPTIONS = [hueOption('with', PHOTO_WORDS.with), hueOption('without', PHOTO_WORDS.without)];

/**
 * One fact in the export dialog's "what this file will contain" block — label left, value right.
 *
 * The dialog carries no period picker and no staff tick-list any more (this register's own filters
 * are the file's), so this block is the only place a reader can check the scope before downloading.
 */
function ScopeRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-16 shrink-0 pt-px type-label text-subtle">{label}</span>
      <span className="min-w-0 flex-1 type-body-medium text-content">{children}</span>
    </div>
  );
}

/** Delegates to the shared StatCard. The hand-rolled version hardcoded `#888` for the label, which
 *  is near-invisible on the dark surface, and sized its own type inline.
 *
 *  Takes a `tone` rather than an accent hex. The static tiles now name their tone, so the row can't
 *  drift back into two identically-green cards; only "Avg effectiveness" still derives one, because
 *  its colour genuinely depends on the score (see `effColor`). */
function Kpi({ title, value, tone, icon, hint, onClick, active }: {
  title: string; value: React.ReactNode; tone: StatTone; icon: React.ReactNode;
  hint?: React.ReactNode; onClick?: () => void; active?: boolean;
}) {
  return <StatCard label={title} value={value} tone={tone} icon={icon} hint={hint} onClick={onClick} active={active} />;
}

export function VisitsPage() {
  const { token } = theme.useToken();
  const [range, setRange] = useUrlRange('range', [dayjs().subtract(7, 'day'), dayjs()]);
  const [userId, setUserId] = useUrlString<string | undefined>('user', undefined);
  const [visitType, setVisitType] = useUrlString<string | undefined>('visitType', undefined);
  const [hasOrder, setHasOrder] = useUrlString<string | undefined>('hasOrder', undefined);
  const [geofence, setGeofence] = useUrlString<string | undefined>('geofence', undefined);
  /*
   * The rest of the register, made filterable — each one narrows a column the table already prints,
   * and each is applied by the SERVER, so the list, the tiles and the exported file all obey it.
   *
   * `status` and `quality` used to be a hidden client-side `subset` that only the Completed and Avg
   * quality tiles could set: the table narrowed with no control on screen saying so, and the file had
   * to be taught a second vocabulary (`completed`, `maxQuality`) to follow it. They are ordinary
   * filters now, and those two tiles simply set them.
   *
   * `search` is its own key rather than `q`: the Coverage tabs on this same URL already own `q`, and a
   * search typed here must not quietly narrow a different register.
   */
  const [searchInput, setSearchInput] = useUrlString('search');
  const search = useDebounce(searchInput, 400).trim();
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  const [quality, setQuality] = useUrlString<string | undefined>('quality', undefined);
  const [photos, setPhotos] = useUrlString<string | undefined>('photos', undefined);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // "List" = flat sortable table; "By day" = the same visits grouped under date headers so it's easy
  // to see everything done on a given day at a glance; "Customers" = the distinct parties those
  // visits reached — "which customers?", answered as a register rather than as a dialog laid over
  // the page it was answering about (you could not sort it, page it, or keep it while you read).
  // In the URL so drilling into a visit and pressing Back returns to the view you were in.
  const [viewMode, setViewMode] = useUrlTab('view', ['list', 'day', 'customers'] as const, 'list');
  const onCustomers = viewMode === 'customers';
  const canManage = usePermission('VISIT.MANAGE');
  /*
   * Beat Coverage lives here as three further tabs rather than as its own nav entry: "who did we
   * visit" and "who is still owed a visit" are two halves of one question, and having them on
   * separate pages meant cross-navigating to compare them.
   *
   * The two permissions are granted together today, but a custom role can hold either alone — so the
   * tab strip only offers what the actor may actually open, and the landing section follows suit.
   */
  const canSeeVisits = usePermission('VISIT.VIEW');
  const canSeeCoverage = usePermission('COVERAGE.VIEW');
  const [section, setSection] = useUrlTab(
    'section',
    ['visits', 'coverage', 'schedule', 'norms'] as const,
    canSeeVisits ? 'visits' : 'coverage',
  );
  const onVisits = section === 'visits';
  /*
   * The planner's own settings (whose week, which week, week/month) leave with the planner. They stayed in the
   * address, so a Coverage link copied after using the planner carried `schedUser=231` — a filter the Coverage
   * tab never reads, naming a rep nobody picked there (V-27).
   */
  const [, setSearchParams] = useSearchParams();
  /*
   * What each tab keeps in the address, dropped when that tab is left. The territory cascade (state / district / beat) is
   * deliberately NOT here: Coverage hands it to the Schedule tab. A table's `sort` and `page` belong to the table on screen,
   * so they never travel either — the register's `sort=-quality` was riding along onto Coverage and Schedule.
   */
  const SECTION_PARAMS: Record<string, string[]> = {
    visits: ['range', 'search', 'view'],
    coverage: ['grain', 'q', 'noVisit', 'window', 'focus', 'rep'],
    schedule: ['schedUser', 'week', 'plan', 'date'],
    norms: ['newRule'],
  };
  const pickSection = (v: string) => {
    if (v !== section) {
      patchSearchParams(
        (buf) => { for (const k of [...(SECTION_PARAMS[section] ?? []), 'sort', 'page']) buf.delete(k); },
        (final) => setSearchParams(final, { replace: true }),
      );
    }
    setSection(v);
  };
  const canViewUsers = usePermission('USER.VIEW');
  // Logging a visit ON BEHALF of a rep is SA-only by default via the dedicated FIELD.ONBEHALF
  // permission (editable in Roles & Permissions) — separate from the broad VISIT.MANAGE.
  const canOnBehalf = usePermission('FIELD.ONBEHALF');
  // Matches the server's VISIT.EXPORT gate — VISIT.VIEW is held by a rep for their own day.
  const canExport = usePermission('VISIT.EXPORT');
  const [exportOpen, setExportOpen] = useState(false);

  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort();

  const params = useMemo(() => {
    const p: Record<string, string> = {
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
    };
    if (userId) p.userId = userId;
    if (search) p.search = search;
    if (visitType) p.visitType = visitType;
    if (status) p.status = status;
    if (quality) p.quality = quality;
    if (hasOrder) p.hasOrder = hasOrder;
    if (geofence) p.geofence = geofence;
    if (photos) p.photos = photos;
    if (sort) p.sort = sort;
    return p;
  }, [range, userId, search, visitType, status, quality, hasOrder, geofence, photos, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['visits', params],
    queryFn: () => request<any[]>({ url: '/visits', params }),
    // The coverage tabs never read this list, and it is the heaviest call on the page.
    enabled: onVisits,
    placeholderData: keepPreviousData,
  });

  const { data: users } = useQuery({
    // Field staff = Sales Persons AND Sales Heads (a Head also runs his own visits) → both can have a
    // visit logged on their behalf.
    queryKey: ['field-staff-users'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON,SALES_HEAD' }),
    // /users needs USER.VIEW. A Sales Person can open this page to see their OWN rows but cannot list
    // staff, so firing this unconditionally logged a 403 for them on every visit.
    enabled: canViewUsers,
  });

  /* Every filter is applied by the server now, so the rows ARE the list. A visit carries `quality`
     only once it has completed — see docs/VISIT-DATA-CONTRACT.md. */
  const rows = data ?? [];
  // KPI tiles reflect the SCOPE only — date range, staff and search — NOT the category filters (type,
  // status, quality, geofence, order, photos): those break the tiles down, so selecting one would zero
  // the others. Separate scoped fetch.
  const kpiParams = useMemo(() => {
    const p: Record<string, string> = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
    if (userId) p.userId = userId;
    if (search) p.search = search;
    return p;
  }, [range, userId, search]);
  const { data: kpiData } = useQuery({ queryKey: ['visits-kpi', kpiParams], queryFn: () => request<any[]>({ url: '/visits', params: kpiParams }), enabled: onVisits, placeholderData: keepPreviousData });
  const kpiRows = kpiData ?? [];
  const kpis = useMemo(() => {
    const withOrder = kpiRows.filter((r: any) => r.orderPlaced).length;
    const outGeo = kpiRows.filter((r: any) => !r.checkInWithinGeofence).length;
    /*
     * COMPLETED uses the shared definition the API now stamps on every row (`quality` is only
     * computed for a completed visit — see docs/VISIT-DATA-CONTRACT.md). Counting rows was wrong:
     * an en-route or cancelled visit is not work done, and this page was the last surface still
     * implying it was.
     */
    const completed = kpiRows.filter((r: any) => r.quality).length;
    const open = kpiRows.length - completed;
    /*
     * Distinct customers reached — three calls on one dealer is one customer covered.
     *
     * Kept as the LIST, not just its length, because a bare count invites the only question a reader
     * ever has about it: which three? Everything the answer needs is already on the rows.
     */
    type CustomerRow = {
      key: string; name: string; type: string; place: string | null;
      retailerId?: string; farmerId?: string; visits: number; last: string | null;
      /** WHO made the most recent call, so the row names a person and not just a count. */
      lastBy: string | null; lastById: string | null; lastByRole: string | null;
      /** Everyone who called on this customer in the window, first seen first. A customer can be
       *  worked by a rep and their Head in the same week, and "2 visits" with one name on the row
       *  would credit both calls to whoever happened to go last. */
      staff: Array<{ id: string; name: string; role: string | null }>;
      /** The rep this customer actually BELONGS to, which is often not the one who called. Null when
       *  nobody owns them yet. */
      owner: { id: string; name: string; role: string | null } | null;
    };
    const byCustomer = new Map<string, CustomerRow>();
    for (const r of kpiRows as any[]) {
      if (!r.retailerId && !r.farmerId) continue;
      const key = `${r.visitType}-${r.retailerId ?? r.farmerId}`;
      const cur = byCustomer.get(key);
      const who = { id: String(r.userId), name: r.staffName ?? `#${r.userId}`, role: r.staffRole ?? null };
      if (cur) {
        cur.visits += 1;
        if (!cur.staff.some((x) => x.id === who.id)) cur.staff.push(who);
        /* The LATEST call wins the "last visit" cell, and its caller comes with it: the two are one
           fact and must move together, or the row prints a date from one visit beside a person from
           another. */
        if (!cur.last || (r.checkInAt && r.checkInAt > cur.last)) {
          cur.last = r.checkInAt ?? cur.last;
          cur.lastBy = who.name; cur.lastById = who.id; cur.lastByRole = who.role;
        }
      } else {
        byCustomer.set(key, {
          key, name: r.party?.name ?? `#${r.retailerId ?? r.farmerId}`, type: r.visitType,
          place: r.party?.location ?? null, retailerId: r.retailerId, farmerId: r.farmerId,
          visits: 1, last: r.checkInAt ?? null,
          lastBy: who.name, lastById: who.id, lastByRole: who.role, staff: [who],
          owner: r.party?.owner ?? null,
        });
      }
    }
    const customerList = [...byCustomer.values()].sort((a, b) => b.visits - a.visits || a.name.localeCompare(b.name));
    const customers = customerList.length;
    // Quality is the SHARED score, not the legacy `effectiveness_score` column (a second heuristic
    // with different weights). Averaged over completed visits only — an open call has nothing to judge.
    const qs = kpiRows.map((r: any) => Number(r.quality?.score)).filter((n: number) => Number.isFinite(n));
    const avgQuality = qs.length ? Math.round(qs.reduce((a: number, n: number) => a + n, 0) / qs.length) : null;
    return { total: kpiRows.length, withOrder, outGeo, completed, open, customers, customerList, avgQuality };
  }, [kpiRows]);

  // KPI tiles double as one-click filters (same idiom as the attendance + payments tiles): a tile sets
  // the filter it counts, clicking it again clears it, and "Visits" is the all-tile that clears both.
  // The tiles' own numbers never move when you click one — they come from `kpiParams`, which
  // deliberately drops the category filters, so the row still reads as a breakdown of the same set.
  // "Avg effectiveness" stays a plain tile on purpose: it is an average, not a subset, so there is no
  // honest set of rows to filter to.
  const focus = {
    all: !hasOrder && !geofence && !visitType && !status && !quality && !photos,
    withOrder: hasOrder === 'true',
    outGeo: geofence === 'out',
    completed: status === 'completed',
    lowQuality: quality === 'weak',
  };
  // A tile's click REPLACES the category filters rather than stacking onto them, so it opens exactly
  // the rows it counted. Scope (dates, staff, search) stays — the tile numbers are counted inside it.
  const clearFocus = () => {
    setHasOrder(undefined); setGeofence(undefined); setVisitType(undefined);
    setStatus(undefined); setQuality(undefined); setPhotos(undefined);
  };
  const focusOn = (apply: () => void) => { clearFocus(); apply(); };
  /* Moving to the customers roll-up drops the category focus too. That list is the tile's own fetch
     (`kpiParams`), which does not honour those filters — and a tile left lit "filtering" beside a
     list it cannot filter is the page telling two different stories at once. Exactly the clearing
     every other tile already does through `focusOn`. */
  const showCustomers = () => { clearFocus(); setViewMode('customers'); };

  /** The default window this page opens on — anything else counts as a filter the user applied. */
  const DEFAULT_DAYS = 7;
  const filtersActive = !!(userId || searchInput.trim() || visitType || status || quality || geofence || hasOrder || photos)
    || range[0].format('YYYY-MM-DD') !== dayjs().subtract(DEFAULT_DAYS, 'day').format('YYYY-MM-DD')
    || range[1].format('YYYY-MM-DD') !== dayjs().format('YYYY-MM-DD');
  const clearAllFilters = () => {
    clearFocus();   // the six category filters
    setUserId(undefined);
    setSearchInput('');
    setRange([dayjs().subtract(DEFAULT_DAYS, 'day'), dayjs()]);
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'visitNo', title: 'Visit No', dataIndex: 'visitNo', sortable: true, alwaysVisible: true, width: 130, render: (v: string) => <span className="whitespace-nowrap">{v}</span> },
    {
      key: 'staff', title: 'Staff',
      // A name alone does not say whether this call was a rep's own beat work or a Head's. The role
      // pill is the app-wide way of answering that (components/RoleBadge.tsx), so it belongs here too.
      render: (_: any, r: any) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <UserLink id={r.userId} role={r.staffRole} className="truncate">{r.staffName ?? `#${r.userId}`}</UserLink>
          <RoleBadge role={r.staffRole} />
        </span>
      ),
    },
    {
      key: 'party', title: 'Visited', alwaysVisible: true,
      /* Three facts, each in its own place so a column of them can be scanned: WHO (a type-coloured
         mark + the name, which opens the record), HOW TO REACH THEM (the handset — a farmer's only
         identifier), and WHERE (the State → District → Beat chips every customer register uses). They
         used to run together as one grey line under a name, and rows could not be told apart. */
      render: (_: any, r: any) => r.party ? (() => {
        const hue = TAG_TONE_HUE[TYPE_COLOR[r.visitType] ?? 'neutral'];
        return (
          <div className="flex min-w-0 items-center gap-2.5 py-0.5">
            <span
              className="grid size-8 shrink-0 place-items-center rounded-full"
              style={{ background: `${hue}1f`, color: hue }}
              title={TYPE_WORDS[r.visitType] ?? r.visitType}
              aria-hidden
            >
              {r.visitType === 'FARMER' ? <Sprout size={15} /> : <Store size={15} />}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                {/* The name IS the way into the record. Both links degrade to plain text without the permission. */}
                <span className="truncate font-semibold">
                  {r.visitType === 'FARMER'
                    ? <FarmerLink id={r.farmerId}>{r.party.name ?? '—'}</FarmerLink>
                    : <RetailerLink id={r.retailerId}>{r.party.name ?? '—'}</RetailerLink>}
                </span>
                {r.visitType && (
                  <Tag tone={TYPE_COLOR[r.visitType] ?? 'neutral'} style={{ marginInlineEnd: 0 }}>
                    {TYPE_WORDS[r.visitType] ?? r.visitType}
                  </Tag>
                )}
              </div>
              <div className="mt-1 flex min-w-0 items-center gap-2 whitespace-nowrap">
                {r.party.mobile && (
                  <span className="inline-flex items-center gap-1 text-[11.5px] tabular-nums" style={{ color: token.colorTextSecondary }}>
                    <Phone size={11} aria-hidden />{displayMobile(r.party.mobile)}
                  </span>
                )}
                <TerritoryChips
                  territoryId={r.party.territoryId}
                  fallbackPath={r.party.territoryPath}
                  fallbackName={r.party.territoryPath?.length ? null : (r.party.location ? placeLabel(r.party.location) : null)}
                  city={r.party.city}
                  hideWhenEmpty
                />
              </div>
            </div>
          </div>
        );
      })() : <span style={{ color: token.colorTextTertiary }}>—</span>,
    },
    {
      key: 'visitDate', title: 'Date', dataIndex: 'checkInAt', sortable: true, width: 110,
      // The date, and — on a day the rep punched in more than once — which shift the visit was made in.
      render: (v: string, r: any) => (
        <div className="flex flex-col items-start gap-1">
          <span className="whitespace-nowrap">{formatIstDate(v)}</span>
          <ShiftChip no={r.shiftNo} count={r.shiftCount} />
        </div>
      ),
    },
    { key: 'checkInAt', title: 'Check-in', dataIndex: 'checkInAt', sortable: true, render: (v: string) => formatIstTime(v) },
    { key: 'checkOutAt', title: 'Check-out', dataIndex: 'checkOutAt', sortable: true, render: (v: string) => v ? formatIstTime(v) : <Tag tone="blue">Open</Tag> },
    { key: 'durationMin', title: 'Duration', dataIndex: 'durationMin', sortable: true, align: 'right' as const, render: (v: number) => v != null ? `${v} min` : '—' },
    {
      /* The SHARED quality score — the one the visit's own dialog, the Avg quality tile, the quality filter and the
         export print. This column used to show the legacy `effectiveness_score` (different weights), so a visit
         read "Effectiveness 95" in the list and "Quality 55%" when opened (V-07). Sorted server-side by the same score. */
      key: 'quality', title: 'Quality', dataIndex: 'quality', sortable: true, align: 'right' as const,
      render: (q: any) => (q && Number.isFinite(Number(q.score))
        ? <Tag tone={effTone(Number(q.score))}>{Math.round(Number(q.score))}%</Tag>
        : <span className="text-subtle">—</span>),
    },
    {
      key: 'checkInWithinGeofence', title: 'Geofence', dataIndex: 'checkInWithinGeofence',
      render: (v: boolean) => v ? <Tag tone="green">In</Tag> : <Tag tone="red">Out</Tag>,
    },
    {
      key: 'orderPlaced', title: 'Order', dataIndex: 'orderPlaced',
      render: (v: boolean) => v ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>,
    },
    // NO "Expense" column here, deliberately. A visit no longer has a cost — the FIELD DAY does, and a
    // day's lines usually carry no visit tag at all (you can't split one bike trip's fuel between the
    // farmer and the retailer). The only figures this column could show are legacy per-visit report
    // totals, which read as "this call cost ₹1,515" and invite exactly the misreading the day-anchor
    // was built to remove. The day's cost lives on the claim: Field Force → Expense Reports.
    {
      /*
       * The proof itself, previewable in the row.
       *
       * This cell used to be a tag carrying a count — a number is not evidence — and it was not
       * clickable, so a click on it fell through to the row and opened the visit record instead of
       * the photos the reader was pointing at. `AttachmentCell` shows the thumbnails and previews in
       * place, the same way every other attachment in the app behaves.
       */
      key: 'photosCount', title: 'Photos', dataIndex: 'photosCount', defaultVisible: false,
      // Left-aligned, not right: thumbnails are objects, and objects align with the column, not with digits.
      render: (_n: any, r: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <AttachmentCell files={(r.photoUrls ?? []) as string[]} label={`Visit ${r.visitNo ?? ''} photo`} size={28} />
        </span>
      ),
    },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 50,
      render: (_: any, r: any) => (
        <Tooltip label="View details">
          <Button variant="text" size="sm" aria-label="View details" icon={<Eye size={16} />} onClick={() => setDetailId(String(r.id))} />
        </Tooltip>
      ),
    },
  ];

  /*
   * The customers behind the "Customers covered" tile — one row per PARTY, not per visit, which is
   * why they get their own columns rather than a filter over the visit register: "3 visits, last
   * 07 Sep" is a fact about the customer, and no single visit row could carry it.
   */
  const customerColumns: DataTableColumn<any>[] = [
    {
      key: 'name', title: 'Customer', dataIndex: 'name', sortable: true, alwaysVisible: true,
      render: (_: any, c: any) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className={`flex size-7 shrink-0 items-center justify-center rounded-full border border-line ${c.type === 'FARMER' ? 'text-success-600 dark:text-success-500' : 'text-brand-600 dark:text-brand-400'}`}>
            {c.type === 'FARMER' ? <User size={14} /> : <Store size={14} />}
          </span>
          {c.type === 'FARMER'
            ? <FarmerLink id={c.farmerId} className="truncate">{c.name}</FarmerLink>
            : <RetailerLink id={c.retailerId} className="truncate">{c.name}</RetailerLink>}
        </span>
      ),
    },
    {
      key: 'type', title: 'Type', dataIndex: 'type', sortable: true, width: 120,
      render: (t: string) => <Tag tone={TYPE_COLOR[t] ?? 'neutral'}>{t}</Tag>,
    },
    {
      key: 'place', title: 'Location', dataIndex: 'place', sortable: true,
      render: (place: string | null) => (place ? placeLabel(place) : '—'),
    },
    {
      /*
       * WHO did the work. A coverage list that cannot name the person is only half an answer: the
       * question after "which customers were reached" is always "by whom", and answering it used to
       * mean going back to the visit register and filtering it by hand.
       *
       * EVERY rep who called in the window, not only the last one. A dealer worked by a rep and by
       * their Head in the same week is exactly the case where a single name would hand one person's
       * call to the other. The role pill is the app-wide way of saying which kind of staff this was.
       */
      key: 'staff', title: 'Visited by', dataIndex: 'lastBy', sortable: true,
      render: (_: any, c: any) => {
        const staff: Array<{ id: string; name: string; role: string | null }> = c.staff ?? [];
        if (!staff.length) return <span className="text-subtle">—</span>;
        const [first, ...rest] = staff;
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <UserLink id={first.id} role={first.role ?? undefined} className="truncate">{first.name}</UserLink>
            <RoleBadge role={first.role ?? undefined} />
            {rest.length > 0 && (
              <Tooltip label={rest.map((x) => `${x.name}${x.role ? ` · ${roleLabel(x.role)}` : ''}`).join('\n')}>
                <span className="shrink-0 text-xs text-subtle">+{rest.length}</span>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      /*
       * WHOSE customer this is, beside who called on them. The two are different questions and the
       * answer is regularly different: a rep calling on a dealer that belongs to another rep is
       * ordinary work, but with only "Visited by" on the row it looked like the coverage figures
       * were contradicting the visit list. Naming the owner here settles it on sight.
       *
       * "Nobody yet" is printed rather than a dash: an unassigned customer is a real, actionable
       * state (somebody has to be given them), not missing information.
       */
      key: 'owner', title: 'Assigned to', dataIndex: 'owner', sortable: true,
      render: (_: any, c: any) => {
        const o = c.owner;
        if (!o) {
          return (
            <Tooltip label="No sales person or head is assigned to this customer yet, so calling on them counts as work but not as anyone's book coverage.">
              <span className="text-subtle">Nobody yet</span>
            </Tooltip>
          );
        }
        const visitedByOwner = (c.staff ?? []).some((x: any) => String(x.id) === String(o.id));
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <UserLink id={o.id} role={o.role ?? undefined} className="truncate">{o.name}</UserLink>
            <RoleBadge role={o.role ?? undefined} />
            {!visitedByOwner && (
              <Tooltip label={`Called on by someone else this window, not by ${o.name}. The call counts as the visitor's work; the book coverage stays with the assigned staff member unless a coverage grant is in force.`}>
                <Tag tone="gold">Not their own</Tag>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      key: 'visits', title: 'Visits', dataIndex: 'visits', sortable: true, align: 'right', width: 110,
      render: (n: number) => `${n} visit${n === 1 ? '' : 's'}`,
    },
    {
      /*
       * WHEN, to the minute. A bare date cannot separate two calls made on the same day, which is
       * the exact shape of the data this list folds up, so the check-in TIME sits beside the date.
       * When more than one rep worked this customer the last caller is named here too, because then
       * "Visited by" holds several people and the date alone would not say which of them was last.
       */
      key: 'last', title: 'Last visit', dataIndex: 'last', sortable: true, align: 'right', width: 190,
      render: (v: string | null, c: any) => {
        if (!v) return <span className="text-subtle">—</span>;
        const many = (c.staff ?? []).length > 1;
        return (
          <Tooltip label={`Most recent call on this customer in the window — ${formatIst(v)}${c.lastBy ? ` by ${c.lastBy}` : ''}.`}>
            <span className="inline-block text-right">
              <span className="type-body-medium text-content">{formatIstDate(v)}</span>
              <span className="text-subtle"> · {formatIstTime(v)}</span>
              {many && c.lastBy && <div className="text-xs text-subtle">by {c.lastBy}</div>}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  /* ── What the EXPORT is ─────────────────────────────────────────────────────────────────────────
     The page's own filters, read back in words, in the order the toolbar lays them out. The server
     prints the same lines into the file, so the dialog and the document describe one scope. */
  const staffLabel = userId
    ? ((users?.data ?? []) as any[]).find((u) => String(u.id) === String(userId))?.fullName
      ?? rows.find((r: any) => String(r.userId) === String(userId))?.staffName
      ?? 'The selected staff member'
    : 'Everyone in your scope';
  const exportFilterText = [
    search ? `Matching “${search}”` : null,
    visitType ? `Type: ${TYPE_WORDS[visitType] ?? visitType}` : null,
    status ? `Status: ${STATUS_WORDS[status] ?? status}` : null,
    quality ? `Quality: ${QUALITY_WORDS[quality] ?? quality}` : null,
    geofence ? GEOFENCE_WORDS[geofence] ?? `Geofence: ${geofence}` : null,
    hasOrder ? ORDER_WORDS[hasOrder] ?? `Order: ${hasOrder}` : null,
    photos ? PHOTO_WORDS[photos] ?? `Photos: ${photos}` : null,
  ].filter(Boolean) as string[];
  // The sort in words. `-durationMin` is the wire form; the reader knows the column by its heading.
  const sortLabel = sortState
    ? `${columns.find((c) => c.key === sortState.key)?.title ?? sortState.key}, ${sortState.dir === 'asc' ? 'ascending' : 'descending'}`
    : 'Newest check-in first';
  const exportScope = (
    <div className="space-y-1">
      <ScopeRow label="Period">{range[0].format('DD MMM YYYY')} – {range[1].format('DD MMM YYYY')}</ScopeRow>
      <ScopeRow label="Staff">{staffLabel}</ScopeRow>
      <ScopeRow label="Filters">{exportFilterText.length ? exportFilterText.join(' · ') : 'None'}</ScopeRow>
      <ScopeRow label="Order">{sortLabel}</ScopeRow>
      {/* The unit is a VISIT on every view: By day groups the same rows under dates, and Customers
          folds them into parties — the file is the flat visit register either way. */}
      <ScopeRow label="Rows">
        {rows.length.toLocaleString('en-IN')} visit{rows.length === 1 ? '' : 's'} —{' '}
        {viewMode === 'list' ? 'exactly what the list below is showing'
          : viewMode === 'day' ? 'the visits grouped by day below'
            : 'the visits behind the customers listed below'}
      </ScopeRow>
    </div>
  );

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Visits &amp; Coverage</Typography.Title>
        {/* View mode belongs to the visit REGISTER, so it only appears with it. */}
        {onVisits && (
          <Segmented
            size="small"
            value={viewMode}
            onChange={(v) => (v === 'customers' ? showCustomers() : setViewMode(String(v)))}
            options={[
              { value: 'list', label: 'List', icon: <List size={14} /> },
              { value: 'day', label: 'By day', icon: <CalendarDays size={14} /> },
              { value: 'customers', label: 'Customers', icon: <Store size={14} /> },
            ]}
          />
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {onVisits && canExport && (
            <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
          )}
          {onVisits && canOnBehalf ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>Log visit</Button>
          ) : undefined}
        </div>
      </div>

      {/* The four tabs are wider than a phone. They scroll INSIDE this strip rather than pushing the
          page body sideways — a horizontally scrolling page loses the right edge of every table. */}
      <div className="-mx-1 mb-3 overflow-x-auto px-1 pb-1">
      <Segmented
        value={section}
        onChange={(v) => pickSection(String(v))}
        options={[
          ...(canSeeVisits ? [{ value: 'visits', label: 'Visits', icon: <Crosshair size={14} /> }] : []),
          ...(canSeeCoverage ? [
            { value: 'coverage', label: 'Coverage', icon: <Target size={14} /> },
            { value: 'schedule', label: 'Schedule', icon: <CalendarRange size={14} /> },
            { value: 'norms', label: 'Visit Scheduler', icon: <Repeat size={14} /> },
          ] : []),
        ]}
      />
      </div>

      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {onVisits
          ? 'Field visits to retailers & farmers — geo-verified check-in/out, photos and effectiveness.'
          : section === 'coverage'
            ? 'How often each retailer and farmer must be visited, who is due, and who has been missed.'
            : section === 'schedule'
              ? 'Plan a rep’s week from the customers they still owe a visit.'
              : 'The visit frequency each territory must meet. A rule on a state applies to every beat under it.'}
      </Typography.Paragraph>

      {!onVisits && <CoverageSection section={section as CoverageSectionKey} />}

      {onVisits && (
        <>
        {/*
        KPI band. Every tile that HAS an underlying set of rows is a one-click filter; the two that
        are summaries (an average, a distinct count) are deliberately not clickable, because there is
        no honest set of rows to filter to and a dead-looking control that does nothing is worse than
        a plain figure. Numbers come from `kpiParams`, which drops the category filters, so clicking
        one tile never moves the others.
      */}
      <Row align="stretch" gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={8} lg={4}>
          <Kpi title="Visits" value={kpis.total} tone="neutral" icon={<Crosshair size={16} />}
            hint={focus.all ? 'all visits' : 'show all'}
            active={focus.all} onClick={clearFocus} />
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Kpi title="Completed" value={kpis.completed} tone="brand" icon={<CheckCircle2 size={16} />}
            hint={focus.completed ? 'filtering' : (kpis.open > 0 ? `${kpis.open} still open` : 'show these')}
            active={focus.completed}
            onClick={() => (focus.completed ? clearFocus() : focusOn(() => setStatus('completed')))} />
        </Col>
        <Col xs={12} md={8} lg={4}>
          {/*
            * A distinct COUNT has no subset of visit rows to filter to, so this tile swaps the
            * register for the customers themselves — one row each, in the same table the rest of
            * the page uses. It used to jump to Coverage, which lists the whole customer master
            * with its due/overdue status — a different question entirely, and it left the reader
            * staring at 260 rows wondering which three the tile had meant.
            */}
          {/* "Visited", not "covered": covered means a met visit rule (the Coverage tab); this is distinct customers reached. */}
          <Kpi title="Customers visited" value={kpis.customers} tone="sky" icon={<Store size={16} />}
            hint={kpis.customers ? (onCustomers ? 'listed below' : 'see who') : 'nobody yet'}
            active={onCustomers}
            onClick={() => kpis.customers && (onCustomers ? setViewMode('list') : showCustomers())} />
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Kpi title="With order" value={kpis.withOrder} tone="success" icon={<ShoppingCart size={16} />}
            hint={focus.withOrder ? 'filtering' : 'show these'}
            active={focus.withOrder} onClick={() => (focus.withOrder ? clearFocus() : focusOn(() => setHasOrder('true')))} />
        </Col>
        <Col xs={12} md={8} lg={4}>
          {/* An average cannot be filtered to, but the visits DRAGGING it down can — that is the
              set anyone reading this number actually wants to see. */}
          <Kpi title="Avg quality" value={kpis.avgQuality == null ? '—' : `${kpis.avgQuality}%`}
            tone={kpis.avgQuality == null ? 'neutral' : statTone(effColor(kpis.avgQuality))}
            icon={<Crosshair size={16} />}
            hint={focus.lowQuality ? 'showing under 50%' : 'show under 50%'}
            active={focus.lowQuality}
            onClick={() => (focus.lowQuality ? clearFocus() : focusOn(() => setQuality('weak')))} />
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Kpi title="Outside geofence" value={kpis.outGeo} tone="danger" icon={<MapPin size={16} />}
            hint={focus.outGeo ? 'filtering' : 'show these'}
            active={focus.outGeo} onClick={() => (focus.outGeo ? clearFocus() : focusOn(() => setGeofence('out')))} />
        </Col>
      </Row>

      {/* Filters are shared across all three views. */}
        {(() => {
          const filterControls = (
            <>
              {/* First because it reaches the first column: the visit number, or the customer called
                  on by name or mobile. It narrows the scope the tiles count as well — "Patil's visits"
                  is a population, not a category — so it stays on the Customers view too. */}
              <Input
                prefix={<Search size={16} className="text-subtle" />}
                placeholder="Visit no, customer or mobile" style={{ width: 240 }} allowClear
                aria-label="Search visits by visit number, customer name or mobile"
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              />
              {canViewUsers && (
                /* Wide enough for the names this system actually holds — at 220px
                   "QA-E2E-20260829 Android SalesPerson2 (QA ONLY)" was clipped past reading. The
                   explicit "All staff" row matters too: the placeholder says the same thing, but
                   only until you pick someone, and after that there is no visible way back. */
                <Select
                  placeholder="All staff" allowClear showSearch optionFilterProp="title"
                  optionLabelProp="title"
                  /* WIDTH VIA `style`, NOT A CLASS. A standalone Select sets its own
                     `w-auto min-w-[7rem]`, and `cn()` carries no tailwind-merge — so a `w-[360px]`
                     className simply lost to the component's own rule and every control in this row
                     collapsed to the 112px minimum, however wide the class said. */
                  style={{ width: 360 }} value={userId} onChange={(v: any) => setUserId(v || undefined)}
                  options={[
                    { value: '', title: 'All staff', label: 'All staff' },
                    ...(users?.data ?? []).map((u: any) => {
                      const role = u.role?.name ?? u.role?.code ?? u.roleName ?? u.roleCode ?? null;
                      return {
                        value: String(u.id),
                        // Plain text for search AND for the collapsed control — a Tag inside a 36px
                        // filter would push the row taller than the controls beside it.
                        title: [u.fullName, role ? roleLabel(role) : null].filter(Boolean).join(' · '),
                        label: (
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate">{u.fullName}</span>
                            <RoleBadge role={role} />
                          </span>
                        ),
                      };
                    }),
                  ]}
                />
              )}
              {/* The category filters narrow the VISIT rows. The Customers view is a distinct-
                  party roll-up of the tile's own fetch (`kpiParams`), which deliberately drops those
                  filters — so they are not offered there rather than sitting beside a list they
                  cannot move. Staff and the date window DO apply, and stay. */}
              {!onCustomers && (
                <Select placeholder="Any type" allowClear style={{ width: 130 }} value={visitType} onChange={setVisitType}
                  options={TYPE_OPTIONS} />
              )}
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => r && setRange(r as [Dayjs, Dayjs])} />
              {!onCustomers && (
                <>
                  {/* Status reads the Check-out column (a visit still "Open" there), Quality the score
                      column — the same two sets the Completed and Avg quality tiles select. */}
                  <Select placeholder="Any status" allowClear style={{ width: 140 }} value={status} onChange={setStatus}
                    options={STATUS_OPTIONS} />
                  <Select placeholder="Any quality" allowClear style={{ width: 170 }} value={quality} onChange={setQuality}
                    options={QUALITY_OPTIONS} />
                  <Select placeholder="Geofence" allowClear style={{ width: 130 }} value={geofence} onChange={setGeofence}
                    options={GEOFENCE_OPTIONS} />
                  <Select placeholder="Order?" allowClear style={{ width: 130 }} value={hasOrder} onChange={setHasOrder}
                    options={ORDER_OPTIONS} />
                  {/* "Which calls left no proof?" — the question the Photos column is there to answer. */}
                  <Select placeholder="Photos?" allowClear style={{ width: 140 }} value={photos} onChange={setPhotos}
                    options={PHOTO_OPTIONS} />
                </>
              )}
              {/* One control that undoes everything, shown only when there IS something to undo.
                  Clearing five filters one at a time is the slowest way back to the full list, and
                  the date range is the one people forget is still applied. */}
              {filtersActive && (
                <FilterResetButton size="md" onClick={clearAllFilters} />
              )}
            </>
          );
          return viewMode === 'list' ? (
            <Card>
              <DataTable
                storageKey="visits"
                rowKey="id"
                loading={isLoading}
                dataSource={rows}
                columns={columns}
                sortState={sortState}
                onSortChange={onSortChange}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                // The whole row opens the visit. Hunting for a 32px eye icon to read a record you
                // are already pointing at is a tax on every single lookup.
                onRow={(r: any) => ({ className: 'cursor-pointer', onClick: () => setDetailId(String(r.id)) })}
                toolbarLeft={filterControls}
              />
            </Card>
          ) : viewMode === 'customers' ? (
            /*
             * WHICH customers the tile counted. Same table component as the visit register, so it
             * sorts, pages and picks its columns the same way — the dialog this replaced could do
             * none of that, and it covered the tile whose number it was explaining.
             *
             * The scope line names the window and the staff filter because this list obeys them
             * (it is the tile's own `kpiParams` fetch) and says how repeat calls are counted, which
             * is the only way "2 visits" on a row and "1 customer" on the tile read as one story.
             * It does not restate the visit total — the tile beside it already carries that figure.
             */
            <Card>
              <DataTable
                storageKey="visits-customers"
                rowKey="key"
                loading={isLoading}
                dataSource={kpis.customerList}
                columns={customerColumns}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                toolbarLeft={filterControls}
                toolbarBelow={(
                  <div className="type-caption text-subtle">
                    Customers reached between {range[0].format('DD MMM YYYY')} and {range[1].format('DD MMM YYYY')}
                    {userId ? ' by the selected staff member' : ''} — three calls on one customer are one row here.
                  </div>
                )}
                emptyText="No customer was reached in this window."
              />
            </Card>
          ) : (
            <Card>
              <div className="mb-4 flex min-w-0 flex-wrap items-center gap-2">{filterControls}</div>
              <VisitsByDay rows={rows} loading={isLoading} onOpen={(vid) => setDetailId(vid)} />
            </Card>
          );
        })()}
        </>
      )}

      <VisitDetailModal id={detailId} onClose={() => setDetailId(null)} canManage={canManage} />

      {/*
        The file must be the list. No period and no staff tick-list of its own any more: it sends
        `params` WHOLE — period, staff, search, type, status, quality, geofence, order, photos and the
        column sort, the two tiles' narrowing included because they set those same filters — and the
        server rebuilds the list through the list endpoint's own query. A dialog with nothing left to
        set would say nothing about what it is about to download, so the scope is printed instead.
      */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export visits"
        description="Exactly the visits this page is showing. Change the filters behind this dialog to change the file."
        baseUrl="/visits/export"
        baseParams={params}
        hidePeriod
        formats={['xlsx', 'pdf']}
        formatHint="Excel carries the five quality signals as their own columns · PDF is grouped by rep"
        scopeSummary={exportScope}
      />
      <LogVisitModal open={createOpen} onClose={() => setCreateOpen(false)} users={users?.data ?? []} />
    </div>
  );
}

/**
 * "By day" view — the same visits, grouped under a date header (newest day first). Each day header
 * carries a quick roll-up (count, orders, avg effectiveness, off-site, expense) so an admin can track
 * what a rep did on a given day without scanning a flat table. Rows read chronologically within a day.
 */
function VisitsByDay({ rows, loading, onOpen }: { rows: any[]; loading: boolean; onOpen: (id: string) => void }) {
  const groups = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const r of rows) {
      const key = toIstDateString(r.checkInAt) || '—';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    }
    return [...m.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1)) // newest day first
      .map(([key, list]) => ({
        key,
        list: [...list].sort((a, b) => String(a.checkInAt).localeCompare(String(b.checkInAt))), // chronological within the day
      }));
  }, [rows]);

  if (loading && !rows.length) return <div className="py-10 text-center text-muted">Loading…</div>;
  if (!rows.length) return <EmptyState title="No visits" description="No field visits in the selected range." />;

  return (
    <div className="space-y-3">
      {groups.map((g) => {
        const count = g.list.length;
        const withOrder = g.list.filter((v) => v.orderPlaced).length;
        const outGeo = g.list.filter((v) => !v.checkInWithinGeofence).length;
        // No day expense total here. Summing per-visit figures would silently UNDER-report: under the
        // day model most costs (fuel, allowance, lodging) carry no visit tag, so they'd be left out
        // entirely. The day's real cost is on its claim — Field Force → Expense Reports.
        // The shared quality score, averaged over the day's completed visits (the ones that have one).
        const scores = g.list.map((v) => Number(v.quality?.score)).filter((n) => Number.isFinite(n));
        const avgEff = scores.length ? Math.round(scores.reduce((s, n) => s + n, 0) / scores.length) : 0;
        const dk = dayjs(g.key);
        return (
          <div key={g.key} className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-surface-2 px-3 py-2">
              <CalendarDays size={16} className="text-brand-600" />
              <span className="type-body-strong text-content">{formatIstDate(g.key)}</span>
              {dk.isValid() && <span className="text-xs text-muted">{dk.format('dddd')}</span>}
              {/* Day roll-up — each figure spelled out so nothing is a bare, unexplained number. */}
              <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                <span><b className="font-semibold text-content">{count}</b> visit{count > 1 ? 's' : ''}</span>
                {withOrder > 0 && <span><b className="font-semibold text-content">{withOrder}</b> with order</span>}
                {scores.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    avg quality <Tag tone={effTone(avgEff)} style={{ marginInlineEnd: 0 }}>{avgEff}%</Tag>
                  </span>
                )}
                {outGeo > 0 && <span className="inline-flex items-center gap-1"><Tag tone="red" style={{ marginInlineEnd: 0 }}>{outGeo} off-site</Tag></span>}
              </div>
            </div>
            <div>
              {g.list.map((v) => {
                const eff = Number(v.quality?.score);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => onOpen(String(v.id))}
                    className="flex w-full items-center gap-3 border-t border-line px-3 py-2.5 text-left transition-colors first:border-t-0 hover:bg-surface-2"
                  >
                    <div className="w-[120px] shrink-0 tabular-nums">
                      <div className="type-body-medium text-content">{formatIstTime(v.checkInAt)}</div>
                      <div className="text-xs text-muted">{v.checkOutAt ? `→ ${formatIstTime(v.checkOutAt)}` : 'open'}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-content">{v.party?.name ?? '—'}</span>
                        {v.visitType && <Tag tone={TYPE_COLOR[v.visitType] ?? 'neutral'} style={{ marginInlineEnd: 0 }}>{v.visitType}</Tag>}
                      </div>
                      <div className="truncate text-xs text-muted">
                        {v.staffName ?? `#${v.userId}`}{v.party?.location ? ` · ${placeLabel(v.party.location)}` : ''}
                      </div>
                    </div>
                    {/* Time spent at the visit. */}
                    <div className="flex w-20 shrink-0 items-center justify-end gap-1 text-sm tabular-nums text-muted" title="Time spent at the visit">
                      <Clock size={12} />{v.durationMin != null ? `${v.durationMin}m` : '—'}
                    </div>
                    {/* Visit quality 0–100% — the shared score, the same as the List view's Quality column. */}
                    <div className="hidden w-20 shrink-0 justify-center sm:flex" title="Visit quality (0–100%)">
                      {Number.isFinite(eff) ? <Tag tone={effTone(eff)} style={{ marginInlineEnd: 0 }}>{Math.round(eff)}%</Tag> : <span className="text-muted">—</span>}
                    </div>
                    {/* Whether check-in was inside the customer's geofence. */}
                    <div className="hidden w-20 shrink-0 justify-center sm:flex" title="Whether check-in was inside the customer's location (geofence)">
                      {v.checkInWithinGeofence ? <Tag tone="green" style={{ marginInlineEnd: 0 }}>On-site</Tag> : <Tag tone="red" style={{ marginInlineEnd: 0 }}>Off-site</Tag>}
                    </div>
                    {/* Whether an order was placed on this visit. */}
                    <div className="flex w-17 shrink-0 justify-center" title="Whether an order was placed on this visit">
                      {v.orderPlaced ? <Tag tone="green" style={{ marginInlineEnd: 0 }}>Order</Tag> : <span className="text-muted">—</span>}
                    </div>
                    <Eye size={16} className="shrink-0 text-muted" />
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VisitDetailModal({ id, onClose, canManage }: { id: string | null; onClose: () => void; canManage: boolean }) {
  const qc = useQueryClient();
  const { data: v, isLoading } = useQuery({
    queryKey: ['visit-detail', id],
    queryFn: () => request<any>({ url: `/visits/${id}` }),
    enabled: !!id,
  });
  const [editNotes, setEditNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const meId = useAuthStore((s) => s.user?.id);
  const canManageVisit = usePermission('VISIT.MANAGE');
  const delPhoto = useConfirmedMutation({ title: "Delete this photo?", content: "It is removed from the visit record and can’t be recovered.", okText: "Yes, delete", danger: true }, {
    mutationFn: (photoId: string) => request({ url: `/visits/${id}/photos/${photoId}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Photo removed'); qc.invalidateQueries({ queryKey: ['visit-detail', id] }); qc.invalidateQueries({ queryKey: ['visits'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const saveNotes = useConfirmedMutation({ title: "Save these visit notes?", okText: "Yes, save" }, {
    mutationFn: () => request({ url: `/visits/${id}/notes`, method: 'PATCH', data: { notes: notesDraft } }),
    onSuccess: () => {
      message.success('Notes updated');
      qc.invalidateQueries({ queryKey: ['visit-detail', id] });
      qc.invalidateQueries({ queryKey: ['visits'] });
      setEditNotes(false);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const Geo = ({ lat, lng, label }: { lat?: string | null; lng?: string | null; label: string }) => {
    const url = mapsUrl(lat, lng);
    if (!url) return <span>—</span>;
    return <a href={url} target="_blank" rel="noreferrer"><MapPin size={16} /> {label} ({Number(lat).toFixed(4)}, {Number(lng).toFixed(4)})</a>;
  };


  // Valid Indian coords only — treat empty/0 as "not captured".
  const coord = (la: any, lo: any): LatLng | null => {
    const a = Number(la); const b = Number(lo);
    return Number.isFinite(a) && Number.isFinite(b) && a !== 0 && b !== 0 ? { lat: a, lng: b } : null;
  };
  // Four distinct points in a visit's story:
  //  • journeyStart — where the rep set off for THIS customer (the "Start" mark); often the previous
  //    stop, e.g. a farmer they just left. Missing on older / on-behalf visits.
  //  • reached — arrival / check-in at the customer (the "Reached" mark; where the geofence is judged).
  //  • leavePt — departure / check-out.
  //  • sitePt  — the customer's own registered location (green + geofence).
  const journeyStartPt = v ? coord(v.startLat, v.startLong) : null;
  const reachedPt = v ? coord(v.checkInLat, v.checkInLong) : null;
  const leavePt = v ? coord(v.checkOutLat, v.checkOutLong) : null;
  const sitePt = v?.party ? coord(v.party.latitude, v.party.longitude) : null;
  const dTravel = journeyStartPt && reachedPt ? distanceMeters(journeyStartPt, reachedPt) : null;
  const dReachedSite = sitePt && reachedPt ? distanceMeters(sitePt, reachedPt) : null;
  // Only draw a separate "Left" pin when the rep departed from a noticeably different spot than they
  // arrived — otherwise it's the same on-site point and just clutters the map.
  const showLeavePt = leavePt && (!reachedPt || distanceMeters(reachedPt, leavePt) > 20) ? leavePt : null;

  /* The mark colours ARE the map's pin colours, so the timeline, the map and its legend are one key. */
  const MARK = { start: PALETTE.sky[500], site: PALETTE.success[500], reached: PALETTE.warning[600], leave: PALETTE.plum[500] };
  const typeHue = v ? TAG_TONE_HUE[TYPE_COLOR[v.visitType] ?? 'neutral'] : TAG_TONE_HUE.neutral;

  return (
    <Modal
      open={!!id} onOpenChange={(o) => { if (!o) onClose(); }} width={1120}
      title={(
        <span className="flex flex-wrap items-center gap-2">
          {v ? (v.visitNo ?? 'Visit') : 'Visit'}
          {v && <Tag tone={TYPE_COLOR[v.visitType] ?? 'neutral'}>{TYPE_WORDS[v.visitType] ?? v.visitType}</Tag>}
          {v && (v.checkOutAt ? <Tag tone="green">Completed</Tag> : <Tag tone="gold">Open</Tag>)}
          {v && (v.checkInAt ?? v.startedAt) && (
            <span className="type-caption font-normal text-subtle">{formatIstDate(v.checkInAt ?? v.startedAt)}</span>
          )}
          {v && <ShiftChip no={v.shiftNo} count={v.shiftCount} />}
        </span>
      )}
      footer={<Button variant="default" onClick={onClose}>Close</Button>}>
      {isLoading || !v ? null : (
        /* Two columns on a wide screen — WHO and HOW on the left, WHERE and the PROOF on the right — so
           the map and the photos sit beside the facts they back up instead of four scrolls below them.
           One column on a phone, in the same order. */
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <div className="min-w-0 space-y-4">
            {/* WHO was visited, and by whom — the two records a reader most often wants next. */}
            <div className="rounded-xl border p-3.5" style={{ borderColor: `${typeHue}55`, background: `${typeHue}0f` }}>
              <div className="flex min-w-0 items-start gap-3">
                {/* The place itself where there is a picture of it; the type mark where there is not. */}
                {v.party?.photoUrl ? (
                  <img
                    src={v.party.photoUrl}
                    alt={v.party?.name ?? 'Customer'}
                    className="size-11 shrink-0 rounded-full border border-line object-cover"
                    onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span className="grid size-11 shrink-0 place-items-center rounded-full" style={{ background: `${typeHue}1f`, color: typeHue }} aria-hidden>
                    {v.visitType === 'FARMER' ? <Sprout size={18} /> : <Store size={18} />}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="type-body font-semibold">
                    {v.visitType === 'FARMER'
                      ? <FarmerLink id={v.farmerId}>{v.party?.name ?? '—'}</FarmerLink>
                      : <RetailerLink id={v.retailerId}>{v.party?.name ?? '—'}</RetailerLink>}
                  </div>
                  {v.party?.mobile && (
                    <a href={`tel:${v.party.mobile}`} className="mt-0.5 inline-flex items-center gap-1 type-caption tabular-nums text-brand-700 underline-offset-2 hover:underline dark:text-brand-400">
                      <Phone size={12} aria-hidden />{displayMobile(v.party.mobile)}
                    </a>
                  )}
                  {v.party && (
                    <TerritoryChips
                      className="mt-1.5"
                      wrap
                      territoryId={v.party.territoryId}
                      fallbackPath={v.party.territoryPath}
                      fallbackName={v.party.territoryPath?.length ? null : (v.party.location ? placeLabel(v.party.location) : null)}
                      city={v.party.city}
                      hideWhenEmpty
                    />
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-line pt-2.5 type-caption text-subtle">
                <span>Visited by</span>
                <UserLink id={v.userId} role={v.staffRole}>{v.staffName ?? `#${v.userId}`}</UserLink>
                <RoleBadge role={v.staffRole} />
              </div>
            </div>

            {/* The four numbers that describe the call, each with its own mark. */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: 'Duration', icon: <Clock size={14} />, hue: TAG_TONE_HUE.blue,
                  value: <span className="type-body font-semibold">{v.durationMin != null ? `${v.durationMin} min` : '—'}</span> },
                { label: 'Quality', icon: <Target size={14} />, hue: v.quality ? effColor(v.quality.score) : TAG_TONE_HUE.neutral,
                  value: v.quality ? <Tag tone={effTone(v.quality.score)}>{v.quality.score}%</Tag> : <span className="text-subtle">—</span> },
                { label: 'Geofence', icon: <Crosshair size={14} />, hue: v.checkInWithinGeofence ? TAG_TONE_HUE.green : TAG_TONE_HUE.red,
                  value: v.checkInWithinGeofence ? <Tag tone="green">Inside</Tag> : <Tag tone="red">Outside</Tag> },
                { label: 'Order', icon: <ShoppingCart size={14} />, hue: v.orderPlaced ? TAG_TONE_HUE.green : TAG_TONE_HUE.neutral,
                  value: v.orderPlaced && v.orderId
                    ? <Link to={`/orders/${v.orderId}`} className="type-body font-semibold text-brand-700 hover:underline dark:text-brand-300">View order</Link>
                    : v.orderPlaced ? <Tag tone="green">Yes</Tag> : <span className="type-body text-subtle">None</span> },
              ].map((t) => (
                <div key={t.label} className="rounded-lg border border-line bg-surface p-2.5">
                  <div className="mb-1 flex items-center gap-1.5 type-caption text-subtle">
                    <span className="grid size-6 place-items-center rounded-md" style={{ background: `${t.hue}1f`, color: t.hue }} aria-hidden>{t.icon}</span>
                    {t.label}
                  </div>
                  <div>{t.value}</div>
                </div>
              ))}
            </div>

            {/* The 3-mark lifecycle as a TIMELINE — the shape of the visit, not three unrelated rows. */}
            <DetailSection title="How the visit ran">
              <ol className="space-y-0">
                {[
                  { label: 'Set off', at: v.startedAt, lat: v.startLat, lng: v.startLong, show: !!v.startedAt, color: MARK.start },
                  { label: 'Reached the customer', at: v.checkInAt, lat: v.checkInLat, lng: v.checkInLong, show: true, color: MARK.reached },
                  { label: v.checkOutAt ? 'Left' : 'Still on site', at: v.checkOutAt, lat: v.checkOutLat, lng: v.checkOutLong, show: true, color: MARK.leave },
                ].filter((m) => m.show).map((m, i, arr) => (
                  <li key={m.label} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="mt-1 size-3 shrink-0 rounded-full" style={{ background: m.at ? m.color : 'var(--color-line-strong)' }} />
                      {i < arr.length - 1 && <span className="w-px flex-1 bg-line" />}
                    </div>
                    <div className={`flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 ${i < arr.length - 1 ? 'pb-3' : ''}`}>
                      <div className="min-w-0">
                        <div className="type-body font-medium">{m.label}</div>
                        <div className="type-caption text-subtle">{m.at ? formatIst(m.at) : 'not recorded'}</div>
                      </div>
                      {/* A pin with the place named says what it opens; "· map" three times read as a stray word. */}
                      {mapsUrl(m.lat, m.lng) && (
                        <a
                          href={mapsUrl(m.lat, m.lng)}
                          target="_blank"
                          rel="noreferrer"
                          title={`Open where the rep was when they marked "${m.label}"`}
                          className="inline-flex shrink-0 items-center gap-1 type-caption text-brand-700 hover:underline dark:text-brand-300"
                        >
                          <MapPin size={12} className="shrink-0" />
                          See this spot
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </DetailSection>

            {v.quality && (
              <DetailSection
                title="Visit quality"
                extra={<Tag tone={effTone(v.quality.score)}>{v.quality.score}/100</Tag>}
              >
                <Progress percent={v.quality.score} strokeColor={effColor(v.quality.score)} showInfo={false} />
                {/* The SHARED signals (docs/VISIT-DATA-CONTRACT.md §5) — the same score the coverage
                    and performance screens read. */}
                <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                  {[
                    ['GPS verified', v.quality.gpsVerified],
                    ['Photo attached', v.quality.photoUploaded],
                    [`Lasted 10+ min${v.quality.durationMin != null ? ` (${v.quality.durationMin})` : ''}`, v.quality.durationAchieved],
                    ['Notes / feedback', v.quality.feedbackCaptured],
                    ['Order placed', v.quality.orderGenerated],
                  ].map(([label, on]: any) => (
                    <span key={label} className={`inline-flex items-center gap-1 type-caption ${on ? 'text-brand-600 dark:text-brand-400' : 'text-subtle'}`}>
                      {on ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{label}
                    </span>
                  ))}
                </div>
              </DetailSection>
            )}

            {/* No per-visit expense here. A visit has no cost of its own — the FIELD DAY does; the rep
                builds the day's claim from My Day → Day expenses, reviewed at Field Force → Expense Reports. */}

            {canManage && (
              /* Only the notes can be edited at all: the times, the GPS marks and the photos are the
                 evidence the visit happened and are deliberately not editable by anyone. */
              <DetailSection
                title="Notes / outcome"
                extra={!editNotes && (
                  <Button
                    variant="default"
                    size="sm"
                    icon={<Pencil size={14} />}
                    onClick={() => { setNotesDraft(v.notes ?? ''); setEditNotes(true); }}
                  >
                    {v.notes ? 'Edit notes' : 'Add notes'}
                  </Button>
                )}
              >
                {editNotes ? (
                  <>
                    <Input.TextArea rows={3} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} />
                    <Space wrap style={{ marginTop: 8 }}>
                      <Button variant="primary" size="sm" loading={saveNotes.isPending} onClick={() => saveNotes.mutate()}>Save</Button>
                      <Button variant="default" size="sm" onClick={() => setEditNotes(false)}>Cancel</Button>
                    </Space>
                  </>
                ) : (
                  <Typography.Paragraph type={v.notes ? undefined : 'secondary'} style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                    {v.notes || 'No notes'}
                  </Typography.Paragraph>
                )}
              </DetailSection>
            )}
          </div>

          <div className="min-w-0 space-y-4">
            {(journeyStartPt || reachedPt || leavePt) && (
              <DetailSection title="Where this visit happened">
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {dTravel != null && dTravel > 30 && <Tag tone="blue" style={{ whiteSpace: 'normal' }}>Travelled {fmtDist(dTravel)} to reach the customer</Tag>}
                  {dReachedSite != null && (
                    <Tag tone={v.checkInWithinGeofence ? 'green' : 'red'} style={{ whiteSpace: 'normal' }}>
                      Checked in {fmtDist(dReachedSite)} from the customer ({v.checkInWithinGeofence ? 'inside' : 'outside'} the geofence)
                    </Tag>
                  )}
                  {reachedPt && !leavePt && <Tag tone="gold">Still on site</Tag>}
                </div>
                <div className="overflow-hidden rounded-lg border border-line">
                  <VisitRouteMap journeyStart={journeyStartPt} reached={reachedPt} leave={showLeavePt} site={sitePt} height={320} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 type-caption text-subtle">
                  {journeyStartPt && <span className="inline-flex items-center gap-1.5"><MarkDot color={MARK.start} />Started here</span>}
                  {sitePt && <span className="inline-flex items-center gap-1.5"><MarkDot color={MARK.site} />Customer · 100 m geofence</span>}
                  {reachedPt && <span className="inline-flex items-center gap-1.5"><MarkDot color={MARK.reached} />Reached (checked in)</span>}
                  {showLeavePt && <span className="inline-flex items-center gap-1.5"><MarkDot color={MARK.leave} />Left</span>}
                </div>
                {!journeyStartPt && (
                  <div className="mt-1.5 type-caption text-subtle">
                    No journey-start point was captured for this visit (an older or on-behalf entry), so only the on-site location is shown.
                  </div>
                )}
                {!sitePt && (
                  <div className="mt-1.5 type-caption text-subtle">
                    The customer has no saved location, so the distance from the customer isn’t shown.
                  </div>
                )}
              </DetailSection>
            )}

            {(() => {
              const isOwner = String(meId) === String(v.userId);
              const visitAt = v.checkInAt ?? v.startedAt ?? v.createdAt;
              const windowOpen = visitAt ? dayjs().diff(dayjs(visitAt), 'day') <= 7 : false;
              const count = v.photos?.length ?? 0;
              return (
                <DetailSection
                  title={<>Photos <span className="font-normal text-subtle">({count})</span></>}
                  extra={isOwner && windowOpen && count < 10 && (
                    <VisitPhotoCapture size="sm" visitId={String(v.id)} remaining={10 - count}
                      onAdded={() => { qc.invalidateQueries({ queryKey: ['visit-detail', id] }); qc.invalidateQueries({ queryKey: ['visits'] }); }} />
                  )}
                >
                  {count ? (
                    <Image.PreviewGroup>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-2.5">
                        {v.photos.map((p: any) => {
                          const where = p.gpsLat && p.gpsLong ? (p.withinGeofence ? 'at site' : 'off-site') : 'no location';
                          return (
                            <div key={p.id} className="min-w-0">
                              <div className="relative overflow-hidden rounded-lg border border-line">
                                <Image src={p.url} width={200} height={200} style={{ objectFit: 'cover', width: '100%', height: 'auto', aspectRatio: '1 / 1', display: 'block' }} />
                                {(isOwner || canManageVisit) && (
                                  <button title="Delete photo" onClick={() => delPhoto.mutate(String(p.id))}
                                    className="absolute right-1 top-1 rounded-md bg-black/60 p-1 leading-none">
                                    <Trash2 size={14} color="#fff" />
                                  </button>
                                )}
                              </div>
                              <div className="mt-1 flex items-center justify-between gap-1 type-caption">
                                <span className="tabular-nums text-subtle">{formatIstTime(p.takenAt)}</span>
                                {where === 'no location'
                                  ? <span className="text-subtle">no location</span>
                                  : (
                                    <a href={mapsUrl(p.gpsLat, p.gpsLong)} target="_blank" rel="noreferrer"
                                      className={`inline-flex items-center gap-0.5 hover:underline ${where === 'off-site' ? 'text-warning-600 dark:text-warning-500' : 'text-brand-700 dark:text-brand-300'}`}>
                                      {where === 'off-site' && <AlertTriangle size={11} />}{where}
                                    </a>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Image.PreviewGroup>
                  ) : (
                    <EmptyState title="No photos" description={windowOpen && isOwner ? 'Use "Add photo" to attach geotagged photos.' : (!windowOpen ? 'The 7-day window to add photos has closed.' : undefined)} />
                  )}
                </DetailSection>
              );
            })()}
          </div>
        </div>
      )}
    </Modal>
  );
}

/** One bordered panel per question the visit dialog answers, so its sections read as separate things. */
function DetailSection({ title, extra, children }: { title: ReactNode; extra?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-line bg-surface p-3.5">
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <span className="type-body-strong text-content">{title}</span>
        {extra && <span className="ml-auto">{extra}</span>}
      </div>
      {children}
    </section>
  );
}

/** A legend key in a map pin's own colour. */
function MarkDot({ color }: { color: string }) {
  return <span className="inline-block size-2.5 shrink-0 rounded-full" style={{ background: color }} aria-hidden />;
}

function LogVisitModal({ open, onClose, users }: { open: boolean; onClose: () => void; users: any[] }) {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const visitType = Form.useWatch('visitType', form);

  // The party pickers SEARCH the server as you type (the shared `useRecordSearch` pickers). One page of 200 used to be
  // loaded and filtered in the browser, so every older dealer and farmer was out of reach — with 319 retailers,
  // Saarrthie KSK and Chaitanya KSK could not be picked at all (E2E 15 Sep 2026). The picked party stays in the list.
  const retailerId = Form.useWatch('retailerId', form);
  const farmerId = Form.useWatch('farmerId', form);
  const retailerPick = useRetailerSearch({ key: 'log-visit-retailers', enabled: open, selected: retailerId });
  const farmerPick = useFarmerSearch({ key: 'log-visit-farmers', enabled: open, selected: farmerId });

  const save = useConfirmedMutation({ title: "Save this visit?", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request({
      url: '/visits/admin', method: 'POST',
      data: {
        userId: vals.userId,
        visitType: vals.visitType,
        retailerId: vals.visitType === 'RETAILER' ? vals.retailerId : undefined,
        farmerId: vals.visitType === 'FARMER' ? vals.farmerId : undefined,
        checkInAt: vals.checkInAt.toISOString(),
        checkOutAt: vals.checkOutAt ? vals.checkOutAt.toISOString() : undefined,
        orderId: vals.orderId || undefined,
        notes: vals.notes,
        onBehalfReason: vals.onBehalfReason,
      },
    }),
    onSuccess: () => {
      message.success('Visit logged');
      qc.invalidateQueries({ queryKey: ['visits'] });
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Log visit (on behalf)"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}
        initialValues={{ visitType: 'RETAILER', checkInAt: dayjs() }}>
        <Form.Item name="userId" label="Staff" rules={[{ required: true, message: 'Pick a staff member' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Select staff"
            options={users.map((u: any) => ({ value: String(u.id), label: u.fullName }))} />
        </Form.Item>
        <Form.Item name="visitType" label="Type" rules={[{ required: true }]}>
          <Select options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]} />
        </Form.Item>
        {visitType === 'RETAILER' ? (
          <Form.Item name="retailerId" label="Retailer" rules={[{ required: true, message: 'Pick a retailer' }]}>
            <Select {...CUSTOMER_SELECT_PROPS} filterOption={false} onSearch={retailerPick.onSearch} placeholder="Select retailer"
              notFoundContent={retailerPick.isFetching ? 'Searching…' : 'No retailer matches'}
              options={retailerPick.rows.map((r: any) => customerOption(r, 'RETAILER'))} />
          </Form.Item>
        ) : (
          <Form.Item name="farmerId" label="Farmer">
            <Select allowClear {...CUSTOMER_SELECT_PROPS} filterOption={false} onSearch={farmerPick.onSearch} placeholder="Select farmer (optional)"
              notFoundContent={farmerPick.isFetching ? 'Searching…' : 'No farmer matches'}
              options={farmerPick.rows.map((f: any) => customerOption(f, 'FARMER'))} />
          </Form.Item>
        )}
        <Row gutter={12}>
          {/* A date-and-time field needs ~230px to show its value; two side by side do not fit a phone, so they stack there. */}
          <Col xs={24} sm={12}><Form.Item name="checkInAt" label="Check-in" rules={[{ required: true }]}><DatePicker showTime={{ minuteStep: 5 }} format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="checkOutAt" label="Check-out (optional)"><DatePicker showTime={{ minuteStep: 5 }} format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
        </Row>
        <Form.Item name="orderId" label="Linked order ID (optional)"><Input placeholder="Order id if one was placed" /></Form.Item>
        <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} placeholder="Visit outcome / context…" /></Form.Item>
        <Form.Item name="onBehalfReason" label="Reason for logging on behalf"
          rules={[{ required: true, message: 'A reason is required (audited)' }, { min: 5, message: 'Give a bit more detail' }]}>
          <Input.TextArea rows={2} placeholder="Why are you logging this for the rep? e.g. no network at site, rep forgot to mark…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
